import React, { useState, useEffect } from 'react';
import { X, Trash2, Play, Clock, Star, Film as FilmIcon, Lock, Globe, GripVertical } from 'lucide-react';
import apiService from '../services/apiService';

const PlaylistDetail = ({ playlistId, onClose, onBack, onPlayMovie }) => {
    const [playlist, setPlaylist] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [draggingIndex, setDraggingIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    useEffect(() => {
        loadPlaylist();
    }, [playlistId]);

    const loadPlaylist = async () => {
        try {
            setLoading(true);
            const response = await apiService.getPlaylist(playlistId);

            if (response.success) {
                setPlaylist(response.data);
            } else {
                setError('Impossible de charger la playlist');
            }
        } catch (err) {
            console.error('Error loading playlist:', err);
            setError('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMovie = async (movieId) => {
        try {
            const response = await apiService.removeMovieFromPlaylist(playlistId, movieId);

            if (response.success) {
                loadPlaylist();
                setShowDeleteConfirm(null);
            }
        } catch (err) {
            console.error('Error removing movie:', err);
            alert('Erreur lors de la suppression');
        }
    };

    // ✨ DRAG & DROP HANDLERS
    const handleDragStart = (e, index) => {
        setDraggingIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.currentTarget);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (draggingIndex !== null && draggingIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOverIndex(null);
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();

        if (draggingIndex === null || draggingIndex === dropIndex) {
            setDraggingIndex(null);
            setDragOverIndex(null);
            return;
        }

        // Réorganiser les films localement
        const newMovies = [...playlist.movies];
        const [draggedMovie] = newMovies.splice(draggingIndex, 1);
        newMovies.splice(dropIndex, 0, draggedMovie);

        // Mettre à jour l'état local immédiatement
        setPlaylist({
            ...playlist,
            movies: newMovies
        });

        // Envoyer l'ordre au backend
        try {
            const movieIds = newMovies.map(m => m.id);
            await apiService.reorderPlaylist(playlistId, movieIds);
        } catch (err) {
            console.error('Error reordering playlist:', err);
            // Recharger en cas d'erreur
            loadPlaylist();
        }

        setDraggingIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggingIndex(null);
        setDragOverIndex(null);
    };

    const formatDuration = (minutes) => {
        if (minutes < 60) return `${minutes}min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    };

    const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
    const iconBtnStyle = { width: 36, height: 36, borderRadius: 'var(--r-md)', background: 'transparent', border: 'none', color: 'var(--text-4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 150ms ease, color 150ms ease' };

    if (loading) {
        return (
            <div style={overlayStyle}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid var(--glass-border)', borderTopColor: 'var(--accent)', animation: 'spinSlow 0.8s linear infinite' }} />
            </div>
        );
    }

    if (error || !playlist) {
        return (
            <div style={overlayStyle}>
                <div style={{ background: 'var(--bg-modal)', border: '1px solid var(--glass-border)', borderRadius: 'var(--r-2xl)', padding: 32, textAlign: 'center' }}>
                    <p style={{ color: '#EF4444', marginBottom: 16 }}>{error || 'Playlist non trouvée'}</p>
                    <button onClick={onBack || onClose} className="btn-primary" style={{ padding: '9px 20px' }}>Retour</button>
                </div>
            </div>
        );
    }

    return (
        <div style={overlayStyle}>
            <div style={{
                background: 'var(--bg-modal)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--r-2xl)',
                boxShadow: 'var(--shadow-xl)',
                width: '100%', maxWidth: 1100,
                maxHeight: '90vh',
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                animation: 'scaleIn 0.2s ease-out',
            }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--glass-border)', flexShrink: 0 }}>
                    <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <h2 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{playlist.name}</h2>
                            {playlist.is_public
                                ? <Globe style={{ width: 16, height: 16, color: '#22C55E', flexShrink: 0 }} />
                                : <Lock style={{ width: 16, height: 16, color: 'var(--text-4)', flexShrink: 0 }} />
                            }
                        </div>
                        {playlist.description && <p style={{ color: 'var(--text-4)', fontSize: '0.875rem', marginBottom: 8 }}>{playlist.description}</p>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, color: 'var(--text-4)', fontSize: '0.875rem' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><FilmIcon style={{ width: 14, height: 14 }} />{playlist.movie_count} film{playlist.movie_count > 1 ? 's' : ''}</span>
                            {playlist.total_duration > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Clock style={{ width: 14, height: 14 }} />{formatDuration(playlist.total_duration)}</span>}
                        </div>
                        {playlist.movies?.length > 0 && (
                            <p style={{ color: 'var(--accent-bright)', fontSize: '0.75rem', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <GripVertical style={{ width: 11, height: 11 }} />
                                Glisse-dépose pour réorganiser
                            </p>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {onBack && (
                            <button onClick={onBack} className="btn-ghost" style={{ padding: '7px 14px', fontSize: '0.875rem' }}>Retour</button>
                        )}
                        <button onClick={onClose} style={iconBtnStyle}
                            onMouseEnter={e => { e.currentTarget.style.background = 'var(--glass-hover)'; e.currentTarget.style.color = 'var(--text-1)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-4)'; }}>
                            <X style={{ width: 20, height: 20 }} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                    {playlist.movies?.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '80px 0' }}>
                            <FilmIcon style={{ width: 56, height: 56, color: 'var(--text-4)', margin: '0 auto 16px', opacity: 0.4 }} />
                            <p style={{ color: 'var(--text-2)', fontSize: '1.125rem', fontWeight: 600, marginBottom: 8 }}>Playlist vide</p>
                            <p style={{ color: 'var(--text-4)', fontSize: '0.875rem', marginBottom: 24 }}>Ajoute des films à cette playlist depuis la page d'accueil</p>
                            <button onClick={onBack || onClose} className="btn-primary" style={{ padding: '10px 20px' }}>Parcourir les films</button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5" style={{ gap: 12 }}>
                            {playlist.movies.map((movie, index) => (
                                <div
                                    key={movie.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, index)}
                                    onDragEnd={handleDragEnd}
                                    className="movie-card"
                                    style={{
                                        background: 'var(--glass)',
                                        border: draggingIndex === index
                                            ? '1px solid var(--accent)'
                                            : dragOverIndex === index
                                                ? '2px solid var(--accent-bright)'
                                                : '1px solid var(--glass-border)',
                                        borderRadius: 'var(--r-xl)',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        cursor: 'grab',
                                        opacity: draggingIndex === index ? 0.5 : 1,
                                        transform: dragOverIndex === index ? 'scale(1.03)' : 'none',
                                    }}
                                >
                                    <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 10, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', borderRadius: 'var(--r-sm)', padding: 4, opacity: 0, transition: 'opacity 150ms ease' }} className="drag-handle">
                                        <GripVertical style={{ width: 14, height: 14, color: 'var(--text-4)' }} />
                                    </div>
                                    <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', borderRadius: 'var(--r-sm)', padding: '2px 7px', color: '#fff', fontWeight: 700, fontSize: '0.75rem' }}>
                                        #{index + 1}
                                    </div>

                                    <div style={{ position: 'relative', aspectRatio: '2/3', background: 'var(--bg-elevated)', overflow: 'hidden' }}>
                                        {movie.poster
                                            ? <img src={movie.poster} alt={movie.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FilmIcon style={{ width: 40, height: 40, color: 'var(--text-4)', opacity: 0.4 }} /></div>
                                        }
                                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)', opacity: 0, transition: 'opacity 200ms ease', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0 12px 12px' }} className="card-overlay">
                                            <button onClick={() => onPlayMovie && onPlayMovie(movie)} className="btn-primary" style={{ width: '100%', padding: '8px', fontSize: '0.8125rem', gap: 6 }}>
                                                <Play style={{ width: 13, height: 13 }} />
                                                Regarder
                                            </button>
                                        </div>
                                    </div>

                                    <div style={{ padding: '10px 12px' }}>
                                        <h3 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '0.8125rem', marginBottom: 4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{movie.title}</h3>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                            {movie.year && <span style={{ color: 'var(--text-4)', fontSize: '0.75rem' }}>{movie.year}</span>}
                                            {movie.rating && (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 3, color: '#FCD34D', fontSize: '0.75rem' }}>
                                                    <Star style={{ width: 11, height: 11, fill: 'currentColor' }} />
                                                    {parseFloat(movie.rating).toFixed(1)}
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => setShowDeleteConfirm(movie.id)}
                                            style={{
                                                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                                padding: '7px', borderRadius: 'var(--r-md)',
                                                background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
                                                color: '#EF4444', fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer',
                                                transition: 'background 150ms ease',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.14)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.06)'}
                                        >
                                            <Trash2 style={{ width: 13, height: 13 }} />
                                            Retirer
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {playlist.movies?.length > 0 && (
                    <div style={{ padding: '16px 24px', borderTop: '1px solid var(--glass-border)', background: 'var(--glass)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                        <p style={{ color: 'var(--text-4)', fontSize: '0.875rem' }}>
                            {playlist.movie_count} film{playlist.movie_count > 1 ? 's' : ''} dans cette playlist
                        </p>
                        <button
                            onClick={() => playlist.movies[0] && onPlayMovie?.(playlist.movies[0])}
                            className="btn-primary"
                            style={{ padding: '10px 20px', gap: 8 }}
                        >
                            <Play style={{ width: 16, height: 16 }} />
                            Lancer le marathon
                        </button>
                    </div>
                )}
            </div>

            {showDeleteConfirm && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: 'var(--bg-modal)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--r-xl)', padding: 24, maxWidth: 360, width: '100%', boxShadow: 'var(--shadow-xl)' }}>
                        <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <Trash2 style={{ width: 22, height: 22, color: '#EF4444' }} />
                        </div>
                        <h3 style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '1.125rem', textAlign: 'center', marginBottom: 8 }}>Retirer ce film ?</h3>
                        <p style={{ color: 'var(--text-4)', fontSize: '0.875rem', textAlign: 'center', marginBottom: 24 }}>Le film sera retiré de la playlist uniquement.</p>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setShowDeleteConfirm(null)} className="btn-ghost" style={{ flex: 1, padding: '11px' }}>Annuler</button>
                            <button onClick={() => handleRemoveMovie(showDeleteConfirm)}
                                style={{ flex: 1, padding: '11px', borderRadius: 'var(--r-md)', background: '#EF4444', border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer', transition: 'background 150ms ease' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#DC2626'}
                                onMouseLeave={e => e.currentTarget.style.background = '#EF4444'}>
                                Retirer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlaylistDetail;